import { useState, ReactNode, useRef, useEffect } from 'react'
import { PiCirclesThreePlus } from 'react-icons/pi'

import { CompanyHeader } from './CompanyHeader'
import { Tabs } from '../../Tabs'
import { CompanySidebarFooter } from './CompanySidebarFooter'
import { CustomCompanySidebarFooter } from './CustomCompanySidebarFooter'
import { cn } from 'components/UI/cn'

export interface CompanySidebarHeader {
  name: string
  domain: string
  logoUrl?: string
  isUserCreated?: boolean
}

export interface SidebarTabConfig {
  key: string
  title: string
  content: ReactNode[]
}

interface CompanySidebarProps {
  chainProxyId: number
  header: CompanySidebarHeader
  tabs: SidebarTabConfig[]
  doorId?: number
  address?: string
  phone?: string
}

export function CompanySidebar({
  chainProxyId,
  header,
  tabs,
  doorId,
  address,
  phone,
}: CompanySidebarProps) {
  const [selectedTab, setSelectedTab] = useState(tabs[0]?.key || '')
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<string>('calc(100vh - 8rem)')

  // Dynamically adjust sidebar height as user scrolls
  useEffect(() => {
    const scrollContainer = document.getElementById('app-content-container')
    const BOTTOM_PADDING = 20 // Bottom margin from screen

    const calculateHeight = () => {
      if (!sidebarRef.current) return

      const sidebarRect = sidebarRef.current.getBoundingClientRect()
      const containerRect = scrollContainer?.getBoundingClientRect()

      if (containerRect) {
        const topOffset = sidebarRect.top - containerRect.top
        const availableHeight =
          containerRect.height - topOffset - BOTTOM_PADDING
        setHeight(`${availableHeight}px`)
      } else {
        // Fallback to viewport
        const availableHeight = window.innerHeight - sidebarRect.top - 20
        setHeight(`${availableHeight}px`)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)
    scrollContainer?.addEventListener('scroll', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
      scrollContainer?.removeEventListener('scroll', calculateHeight)
    }
  }, [])

  // Get the current tab's content
  const currentTabContent =
    tabs.find((tab) => tab.key === selectedTab)?.content || []

  return (
    <div
      ref={sidebarRef}
      className="bg-white rounded-lg border border-gray-200 shadow-sm w-[360px] sticky top-4 self-start flex-shrink-0"
    >
      <div
        className="p-[20px] w-[360px] flex flex-col relative"
        style={{ height }}
      >
        {header.isUserCreated && (
          <div className="w-full absolute top-0 left-0 bg-[#710873] rounded-t-lg h-10 flex items-center justify-center text-white font-medium text-xs">
            <PiCirclesThreePlus size={16} className="mr-1" />
            Custom Company
          </div>
        )}
        {/* Company Header + Tabs */}
        <div
          className={cn('flex-shrink-0', {
            'mt-4': header.isUserCreated,
          })}
        >
          <div className="mb-[24px]">
            <CompanyHeader
              companyName={header.name}
              domain={header.domain}
              logoUrl={header.logoUrl}
              isUserCreated={header.isUserCreated}
            />
          </div>
          <div>
            <Tabs
              tabs={tabs.map((t) => ({ key: t.key, title: t.title }))}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              className="border-b border-gray-200"
            />
          </div>
        </div>

        {/* Tab Content - Scrollable */}
        <div className="my-6 space-y-6 flex-1 overflow-y-auto min-h-0">
          {currentTabContent}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0">
          {header.isUserCreated ? (
            <CustomCompanySidebarFooter chainProxyId={chainProxyId} />
          ) : (
            <CompanySidebarFooter
              chainProxyId={chainProxyId}
              companyName={header.name}
              domain={header.domain}
              doorId={doorId}
              address={address}
              phone={phone}
            />
          )}
        </div>
      </div>
    </div>
  )
}
